import { Area, AreaChart, CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

export type SeriePunto = { month: string; total: number; count: number };
export type SerieAgenzia = { agenzia_name: string | null; punti: SeriePunto[] };

const BRAND = "#4B9B2B";
const GRID = "#eee9dc";
const PALETTE = ["#3a7a21", "#4B9B2B", "#6BC839", "#2563eb", "#0F172A", "#94a3b8"];
const eur = (v: number) => "€" + v.toLocaleString("it-IT");

/** Etichetta asse X: bucket giornaliero ("2026-07-15") → "15", mensile → invariato. */
const bucketTick = (k: string) => (k.length === 10 ? String(Number(k.slice(8))) : k);

function EmptyState({ height }: { height: number }) {
  return (
    <div style={{ height }} className="flex items-center justify-center text-sm text-muted-foreground">
      Nessun dato disponibile.
    </div>
  );
}

export function TrendLine<T extends { month: string }>({
  data, height = 224, dataKey = "total" as keyof T, name = "Totale", valueFormatter = eur, color = BRAND,
}: {
  data: T[]; height?: number; dataKey?: keyof T; name?: string;
  valueFormatter?: (v: number) => string; color?: string;
}) {
  if (data.length === 0) return <EmptyState height={height} />;
  return (
    <div style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
          <XAxis dataKey="month" tickFormatter={bucketTick} tick={{ fontSize: 11 }} interval="preserveStartEnd" minTickGap={12} />
          <YAxis tickFormatter={valueFormatter} tick={{ fontSize: 11 }} width={56} />
          <Tooltip formatter={(v: number) => valueFormatter(v)} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          {/* dot solo su serie corte: su 28-31 giorni i pallini affollano la curva */}
          <Area type="monotone" dataKey={dataKey as string} name={name} stroke={color} fill={`${color}14`} strokeWidth={2}
                dot={data.length <= 12 ? { r: 3, strokeWidth: 0, fill: color } : false} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

export function TrendMultiLine({ series, height = 256 }: { series: SerieAgenzia[]; height?: number }) {
  // bucket dalla serie stessa: la granularità la decide il backend (mesi o giorni)
  const months = series[0]?.punti.map((p) => p.month) ?? [];
  if (series.length === 0 || months.length === 0) return <EmptyState height={height} />;
  const rows = months.map((m) => {
    const row: Record<string, number | string> = { month: m };
    series.forEach((s) => {
      row[s.agenzia_name ?? "—"] = s.punti.find((p) => p.month === m)?.total ?? 0;
    });
    return row;
  });
  return (
    <div style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={rows} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
          <XAxis dataKey="month" tickFormatter={bucketTick} tick={{ fontSize: 11 }} interval="preserveStartEnd" minTickGap={12} />
          <YAxis tickFormatter={eur} tick={{ fontSize: 11 }} width={56} />
          <Tooltip formatter={(v: number) => eur(v)} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          {series.map((s, i) => (
            <Line key={s.agenzia_name ?? i} type="monotone" dataKey={s.agenzia_name ?? "—"}
                  stroke={PALETTE[i % PALETTE.length]} strokeWidth={2}
                  dot={months.length <= 12 ? { r: 3, strokeWidth: 0, fill: PALETTE[i % PALETTE.length] } : false} />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
